<script setup lang="ts">
import { ref, watch, useTemplateRef } from "vue";
import type { Map } from "mapbox-gl";
import type { Feature, FeatureCollection } from "geojson";

// Carte des observations d'une espèce par site (hexgrid), mutualisée web ↔ mobile : cadrage initial
// sur les points, plein écran et sélection d'un site. Le consentement Mapbox (slot #disabled) et le
// panneau de détail du site (slot #popup : web WebMapPopupObservations, mobile MobObservationsSiteSheet)
// sont injectés par l'hôte. `mapboxAllowed`, `fullscreenTeleportTo` et `cooperativeGestures` portent
// les seules différences de plateforme (web : autorisé via cookie-consent, body, gestes coopératifs ;
// mobile : toujours autorisé, ion-app, manipulation à un doigt).
const props = withDefaults(
  defineProps<{
    geo: FeatureCollection;
    mapboxAllowed?: boolean;
    online?: boolean;
    fullscreenTeleportTo?: string;
    cooperativeGestures?: boolean;
    isMobile?: boolean;
  }>(),
  { mapboxAllowed: true, online: true, fullscreenTeleportTo: "body", cooperativeGestures: true, isMobile: false }
);

defineSlots<{
  disabled?: () => unknown;
  popup?: (props: { siteId: string | undefined; close: () => void }) => unknown;
}>();

const mapRef = useTemplateRef<{ mapboxMap: Map | undefined }>("mapRef");
const selectedSiteId = ref<string>();
const isFullscreen = ref(false);

let initialBounds: [[number, number], [number, number]] | undefined;
let didInitialFit = false;

function computeBounds(features: Feature[]): [[number, number], [number, number]] | undefined {
  const lngs: number[] = [];
  const lats: number[] = [];
  for (const f of features) {
    if (f.geometry.type !== "Point") continue;
    const [lng, lat] = f.geometry.coordinates as [number, number];
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) continue;
    lngs.push(lng);
    lats.push(lat);
  }
  if (!lngs.length) return undefined;
  lngs.sort((a, b) => a - b);
  lats.sort((a, b) => a - b);
  return [
    [lngs[0]!, lats[0]!],
    [lngs[lngs.length - 1]!, lats[lats.length - 1]!],
  ];
}

function tryInitialFit() {
  if (didInitialFit) return;
  const map = mapRef.value?.mapboxMap;
  if (!map) return;
  const bounds = computeBounds(props.geo.features);
  if (!bounds) return;
  initialBounds = bounds;
  map.fitBounds(bounds, { padding: 48, maxZoom: 4, animate: false });
  didInitialFit = true;
}

function onInit() {
  tryInitialFit();
}

function onMarkerClick(payload: { feature: GeoJSON.Feature }) {
  const id = payload.feature.properties?.id;
  if (!id || payload.feature.geometry.type !== "Point") return;
  selectedSiteId.value = String(id);
}

function closePopup() {
  selectedSiteId.value = undefined;
}

function onFullscreenChange(value: boolean) {
  isFullscreen.value = value;
  if (!value) {
    selectedSiteId.value = undefined;
    const map = mapRef.value?.mapboxMap;
    if (map && initialBounds) {
      map.fitBounds(initialBounds, { padding: 48, maxZoom: 4, animate: false });
    }
  }
}

watch(() => props.geo, () => tryInitialFit());
</script>

<template>
  <ClientOnly>
    <AppMap
      ref="mapRef"
      :markers="geo"
      :active-feature-id="selectedSiteId"
      :mapbox-allowed="mapboxAllowed"
      :online="online"
      :controls-dimmed="isMobile && isFullscreen && selectedSiteId != null"
      mode="hexgrid"
      :interactive="isFullscreen"
      :zoom-buttons="isFullscreen"
      labelled-fullscreen-button
      :geolocate-button="isFullscreen"
      :cooperative-gestures="cooperativeGestures"
      :fullscreen-teleport-to="fullscreenTeleportTo"
      class="aspect-4.5/3 w-full rounded-2xl"
      @init="onInit"
      @marker-click="onMarkerClick"
      @fullscreen-change="onFullscreenChange"
    >
      <template #disabled>
        <slot name="disabled" />
      </template>

      <slot
        v-if="!isMobile && isFullscreen"
        name="popup"
        :site-id="selectedSiteId"
        :close="closePopup"
      />

      <template v-if="isMobile" #pinned>
        <slot name="popup" :site-id="selectedSiteId" :close="closePopup" />
      </template>
    </AppMap>
  </ClientOnly>
</template>
